import { APP_CONFIG, ERROR_MESSAGES } from '../constants';

class OpenAIService {
  // Chat completion for content analysis and conversations
  async createChatCompletion(messages, options = {}) {
    try {
      console.log('🔄 Making secure server-side OpenAI request...');
      
      const response = await fetch('/api/openai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages,
          model: options?.model || APP_CONFIG.DEFAULT_MODEL,
          max_tokens: options?.maxTokens || APP_CONFIG.MAX_TOKENS,
          temperature: options?.temperature || APP_CONFIG.DEFAULT_TEMPERATURE,
          ...options
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'OpenAI API request failed');
      }

      const data = await response.json();
      
      return {
        data: data.data,
        usage: data.usage,
        error: null
      };
    } catch (error) {
      console.error('OpenAI Chat Completion Error:', error);
      return {
        data: null,
        error: error?.message
      };
    }
  }

  // Structured content analysis for Instagram posts
  async analyzeInstagramContent(contentUrl, contentText = '', contentType = 'post') {
    try {
      const systemPrompt = `You are an expert social media content analyst specializing in Instagram content. Analyze the provided content thoroughly and provide structured insights.

Focus Areas:
- Content themes and topics
- Sentiment analysis (positive, negative, neutral)
- Engagement potential (high, medium, low)
- Key messages and takeaways
- Suggested improvements
- Content category suggestions
- Audience insights
- Hashtag recommendations

Provide actionable insights for content creators and marketers.`;

      const userPrompt = `Analyze this Instagram ${contentType}:

URL: ${contentUrl}
Content Text: ${contentText || 'No text content provided'}

Provide a comprehensive analysis covering all focus areas mentioned in your instructions.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2000
      });

      return response;
    } catch (error) {
      console.error('Content Analysis Error:', error);
      return {
        data: null,
        error: error?.message
      };
    }
  }

  // Auto-categorize content for topic assignment
  async categorizeContent(contentText, existingTopics = []) {
    try {
      const topicsText = existingTopics?.map(t => t?.name)?.join(', ') || 'No existing topics';
      
      const systemPrompt = `You are a content categorization expert. Analyze Instagram content and categorize it appropriately.

RULES:
1. If content clearly fits an existing topic, return: EXISTING: [topic_name]
2. If content needs a new topic, suggest a concise name: NEW: [new_topic_name]  
3. If content is unclear or generic, return: MISC

Be consistent and accurate in your categorization. Topic names should be clear, concise, and descriptive.`;

      const userPrompt = `Categorize this Instagram content:

Content: ${contentText}
Existing Topics: ${topicsText}

Examples:
- Sales content → EXISTING: Sales (if exists) or NEW: Sales
- Product review → NEW: Reviews  
- Random personal post → MISC

Return only the classification.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ], {
        model: 'gpt-3.5-turbo', // Fast for classification
        temperature: 0.3,
        max_tokens: 100
      });

      if (response?.error) {
        return { data: 'MISC', error: response?.error };
      }

      const classification = response?.data?.trim();
      return { data: classification, error: null };
    } catch (error) {
      console.error('Content Categorization Error:', error);
      return { data: 'MISC', error: error?.message };
    }
  }

  // Generate topic-based insights and responses
  async generateTopicResponse(userMessage, topicContext, conversationHistory = []) {
    try {
      const systemPrompt = `You are an expert social media content analyst for Swirl. You have access to Instagram content organized by topics and can provide insights, analysis, and answer questions.

Your capabilities:
- Analyze content themes and patterns
- Provide actionable recommendations  
- Generate content summaries
- Create action plans
- Suggest improvements
- Answer specific questions about the content

Be conversational, helpful, and provide specific insights based on the available content.`;

      const contextPrompt = `Topic Content Context:
${topicContext}

Based on this content, respond to the user's question with specific insights and actionable advice.`;

      const messages = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: contextPrompt },
        ...conversationHistory?.slice(-6), // Include recent conversation
        { role: 'user', content: userMessage }
      ];

      const response = await this.createChatCompletion(messages, {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 1500
      });

      return response;
    } catch (error) {
      console.error('Topic Response Error:', error);
      return {
        data: 'I apologize, but I encountered an error processing your request. Please try again.',
        error: error?.message
      };
    }
  }

  // Generate content-specific insights
  async generateContentResponse(userMessage, contentContext, conversationHistory = []) {
    try {
      const systemPrompt = `You are analyzing a specific Instagram post. Provide detailed insights and answer questions about this particular piece of content.

Your focus:
- Specific content analysis
- Performance insights
- Improvement suggestions
- Audience engagement factors
- Content optimization tips

Be specific and reference the actual content when providing insights.`;

      const messages = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Content Context: ${contentContext}` },
        ...conversationHistory?.slice(-4), // Shorter history for specific content
        { role: 'user', content: userMessage }
      ];

      const response = await this.createChatCompletion(messages, {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 1500
      });

      return response;
    } catch (error) {
      console.error('Content Response Error:', error);
      return {
        data: 'I apologize, but I encountered an error analyzing this content. Please try again.',
        error: error?.message
      };
    }
  }

  // Transcribe audio/video content using Whisper
  async transcribeAudio(audioFile) {
    try {
      // Validate file
      if (!audioFile) {
        throw new Error('No audio file provided');
      }

      const response = await openai?.audio?.transcriptions?.create({
        file: audioFile,
        model: 'whisper-1',
        response_format: 'verbose_json',
        language: 'en'
      });

      return {
        data: response?.text || response,
        usage: response?.usage,
        segments: response?.segments,
        error: null
      };
    } catch (error) {
      console.error('Audio Transcription Error:', error);
      return {
        data: null,
        error: error?.message
      };
    }
  }

  // Analyze content for channel detection, tags, and Smart Collection suggestions
  async analyzeContentForChannelAndTags(contentText) {
    try {
      console.log('🔍 analyzeContentForChannelAndTags called');
      
      // Limit input to 6000 characters to avoid token overflow
      const limitedText = contentText ? contentText.slice(0, 6000) : '';
      
      if (!limitedText || limitedText.trim().length < 20) {
        console.warn('⚠️ No valid content text available for analysis');
        return {
          data: {
            summary: 'Content analysis unavailable',
            key_points: [],
            tags: [],
            main_topics: [],
            confidence_score: 0.0,
            suggestedCollections: [],
            content_metadata: {}
          },
          error: 'No content to analyze'
        };
      }

      console.log('📄 Analyzing text length:', limitedText.length);

      // Set 30-second timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('AI analysis timeout after 30s')), 30000)
      );

      const prompt = `Analyze this transcribed content and provide structured analysis:

${limitedText}

Please provide a JSON response with the following structure:
{
  "channel": "detected channel (Instagram, YouTube, TikTok, Twitter, Facebook, Pinterest, Threads, Podcasts, Email, Slack, Web, Recordings)",
  "summary": "2-3 sentence summary of the content",
  "key_points": ["bullet point 1", "bullet point 2", "bullet point 3", "bullet point 4", "bullet point 5"],
  "tags": ["tag1", "tag2", "tag3", "tag4", "tag5"],
  "suggestedCollections": ["collection name 1", "collection name 2"],
  "contentType": "post, reel, story, video, podcast, article, email, message",
  "sentiment": "positive, negative, neutral",
  "keyTopics": ["topic1", "topic2", "topic3"],
  "confidence_score": 0.95,
  "main_topics": ["primary topic", "secondary topic"],
  "content_metadata": {
    "duration": "if applicable",
    "engagement_potential": "high/medium/low",
    "target_audience": "description"
  }
}

Focus on providing accurate and useful analysis of the transcribed content.`;

      const response = await Promise.race([
        this.createChatCompletion([
          {
            role: 'system',
            content: 'You are an expert content analyst. Always respond with valid JSON only, no additional text.'
          },
          {
            role: 'user',
            content: prompt
          }
        ], {
          temperature: 0.3,
          max_tokens: 800
        }),
        timeoutPromise
      ]);

      if (response?.error) {
        throw new Error(response.error);
      }

      // Parse JSON response with robust fallback
      let analysis;
      try {
        let cleanedResponse = response.data;
        
        if (typeof cleanedResponse === 'string') {
          // Extract JSON from markdown if present
          const jsonMatch = cleanedResponse.match(/```json\s*([\s\S]*?)\s*```/) || 
                             cleanedResponse.match(/```\s*([\s\S]*?)\s*```/);
          if (jsonMatch) {
            cleanedResponse = jsonMatch[1].trim();
          }
          
          // Fix common JSON issues
          cleanedResponse = cleanedResponse
            .replace(/,\s*}/g, '}')
            .replace(/,\s*]/g, ']');
          
          analysis = JSON.parse(cleanedResponse);
        } else if (typeof cleanedResponse === 'object' && cleanedResponse !== null) {
          analysis = cleanedResponse;
        } else {
          throw new Error('Invalid response format');
        }
        
        console.log('✅ Successfully parsed AI analysis');
      } catch (parseError) {
        console.error('⚠️ Invalid JSON, returning fallback summary');
        // Return graceful fallback instead of failing
        const fallbackText = typeof response.data === 'string' 
          ? response.data.slice(0, 500) 
          : 'Content analysis completed';
        
        analysis = {
          channel: 'Web',
          summary: fallbackText,
          key_points: ['Content processed successfully'],
          tags: ['content'],
          suggestedCollections: ['General'],
          contentType: 'post',
          sentiment: 'neutral',
          keyTopics: ['general'],
          confidence_score: 0.5,
          main_topics: ['content'],
          content_metadata: { status: 'processed' }
        };
      }

      return {
        data: analysis,
        error: null
      };
    } catch (error) {
      console.error('Content Analysis Error:', error);
      // Always return structured data, never null
      return {
        data: {
          summary: error.message || 'Content analysis failed',
          key_points: [],
          tags: [],
          main_topics: [],
          confidence_score: 0.0,
          suggestedCollections: [],
          content_metadata: { error: error.message }
        },
        error: error?.message || 'Content analysis failed'
      };
    }
  }

  // Generate structured content summaries
  async generateStructuredSummary(contentList, summaryType = 'general') {
    try {
      const contentText = contentList?.map(content => 
        `URL: ${content?.url}\nTitle: ${content?.title || 'No title'}\nDescription: ${content?.description || 'No description'}`
      )?.join('\n\n');

      const summaryPrompts = {
        general: 'Provide a comprehensive summary of all content, highlighting main themes, insights, and patterns.',
        actionable: 'Create actionable steps and recommendations based on the content analysis.',
        creative: 'Generate creative content ideas and suggestions based on the analyzed content.',
        takeaways: 'Extract the top 5 key takeaways and important lessons from this content.'
      };

      const systemPrompt = `You are a content strategist creating structured summaries for Instagram content analysis. ${summaryPrompts?.[summaryType] || summaryPrompts?.general}

Format your response clearly with:
- Clear headings
- Bullet points for key items  
- Specific examples from the content
- Actionable recommendations`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Content to analyze:\n\n${contentText}` }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2000
      });

      return response;
    } catch (error) {
      console.error('Structured Summary Error:', error);
      return {
        data: 'Unable to generate summary at this time. Please try again.',
        error: error?.message
      };
    }
  }

  // Handle image analysis (for Instagram visual content)
  async analyzeImage(imageUrl, analysisType = 'general') {
    try {
      const systemPrompts = {
        general: `You are analyzing an Instagram post image. Provide a detailed description that includes:
1. Any visible text in the image (read all text, captions, labels, quotes, hashtags shown in the image)
2. Objects, people, scenes, and activities depicted
3. Composition, colors, and visual style
4. Main themes, topics, or messages conveyed
5. Any actionable content or key information

Focus on extracting all text content first, then describe the visual elements. This description will be used for AI content analysis, so be thorough and detailed.`,
        engagement: 'Analyze this Instagram image for engagement potential. Focus on visual appeal, composition, and factors that drive likes and comments.',
        brand: 'Analyze this Instagram image from a brand perspective. Consider brand consistency, messaging, and visual identity.',
        accessibility: 'Analyze this Instagram image for accessibility. Describe all visual elements for visually impaired users.'
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o', // Best visual reasoning and OCR
        messages: [
          { 
            role: 'system', 
            content: systemPrompts?.[analysisType] || systemPrompts?.general
          },
          {
            role: 'user',
            content: [
              { 
                type: 'text', 
                text: analysisType === 'general' 
                  ? 'Please analyze this Instagram image in detail. Extract all visible text first (including any captions, quotes, or text overlaid on the image), then describe the visual content, themes, and key information. Be comprehensive as this will be used for content analysis.'
                  : `Please analyze this Instagram image with focus on: ${analysisType}`
              },
              { type: 'image_url', image_url: { url: imageUrl } },
            ],
          },
        ],
        max_tokens: 2000, // Increased for more detailed descriptions
        temperature: 0.7
      });

      return {
        data: response?.choices?.[0]?.message?.content,
        error: null
      };
    } catch (error) {
      console.error('Image Analysis Error:', error);
      return {
        data: null,
        error: error?.message
      };
    }
  }

  // Generate post content based on analysis
  async generatePost(contentAnalysis, postType = 'instagram', tone = 'engaging') {
    try {
      const systemPrompt = `You are a social media content creator specialized in ${postType} posts. Create engaging, high-quality content based on the provided analysis.

Tone: ${tone}
Platform: ${postType}

Requirements:
- Create compelling captions
- Include relevant hashtags
- Optimize for engagement
- Match the specified tone
- Use insights from the analysis

Make the content authentic and engaging for the target audience.`;

      const userPrompt = `Based on this content analysis, create a ${postType} post:

${contentAnalysis}

Generate a complete post including caption, hashtags, and any additional recommendations.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ], {
        model: 'gpt-4o',
        temperature: 0.8, // Higher creativity for content generation
        max_tokens: 1500
      });

      return response;
    } catch (error) {
      console.error('Post Generation Error:', error);
      return {
        data: 'Unable to generate post content at this time. Please try again.',
        error: error?.message
      };
    }
  }

  // Generate downloadable transcript from video/audio content
  async generateDownloadableTranscript(contentItem) {
    try {
      // Processing transcript request

      // Priority 1: If this is an actual file upload, transcribe it directly
      if (contentItem?.file || contentItem?.media_file) {
        // Transcribing uploaded file
        return await this.transcribeActualFile(contentItem);
      }

      // Priority 2: If this is a video URL (Instagram, YouTube, etc.), download and transcribe
      if (contentItem?.url && this.isVideoUrl(contentItem?.url)) {
        // Detected video URL, starting video transcription
        return await this.transcribeInstagramVideo(contentItem?.url, contentItem);
      }

      // Priority 3: If this is a regular URL, analyze content
      if (contentItem?.url) {
        // Analyzing URL content
        return await this.analyzeUrlContent(contentItem);
      }

      // Priority 4: Generate content-based transcript for posts without URLs
      // Generating content-based transcript
      return await this.generateContentBasedTranscript(contentItem);

    } catch (error) {
      console.error('Transcript Generation Error:', error);
      
      // Fallback to content analysis
      try {
        // Falling back to content analysis
        return await this.generateContentBasedTranscript(contentItem);
      } catch (fallbackError) {
        console.error('Fallback transcript generation failed:', fallbackError);
        return {
          data: `Transcription Failed - ${contentItem?.title || 'Content'}

We were unable to generate a transcript for this content.

Error Details: ${error?.message}

Content Information:
- Title: ${contentItem?.title || 'No title available'}
- URL: ${contentItem?.url || 'N/A'}
- Type: ${contentItem?.content_type || 'Unknown'}

Please ensure the content is accessible and try again.`,
          error: error?.message
        };
      }
    }
  }

  // NEW: Download and transcribe Instagram video
  async transcribeInstagramVideo(videoUrl, contentItem) {
    try {
      // Starting Instagram video transcription
      
      // Step 1: Download the video file
      const videoFile = await this.downloadVideoFromUrl(videoUrl);
      
      if (!videoFile) {
        throw new Error('Failed to download video from Instagram URL');
      }

      // Step 2: Extract audio and transcribe with Whisper
      const transcriptionResponse = await this.transcribeAudio(videoFile);
      
      if (transcriptionResponse?.error) {
        throw new Error(transcriptionResponse.error);
      }

      // Step 3: Format as downloadable transcript
      let formattedTranscript = this.formatVideoTranscriptForDownload(
        transcriptionResponse?.data,
        transcriptionResponse?.segments,
        contentItem,
        videoUrl
      );

      return {
        data: formattedTranscript,
        error: null,
        segments: transcriptionResponse?.segments,
        usage: transcriptionResponse?.usage
      };

    } catch (error) {
      console.error('Instagram Video Transcription Error:', error);
      return {
        data: null,
        error: `Video transcription failed: ${error?.message}`
      };
    }
  }

  // NEW: Download video from URL and convert to audio
  async downloadVideoFromUrl(videoUrl) {
    try {
      // For Instagram URLs, we need to extract the actual video file URL
      const actualVideoUrl = await this.extractInstagramVideoUrl(videoUrl);
      
      if (!actualVideoUrl) {
        throw new Error('Could not extract video file URL from Instagram');
      }

      // Download the video file
      const response = await fetch(actualVideoUrl, {
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
        }
      });

      if (!response?.ok) {
        throw new Error(`Failed to download video: ${response?.status} ${response?.statusText}`);
      }

      const videoBlob = await response?.blob();
      
      // Convert blob to File object for Whisper API
      const file = new File([videoBlob], 'instagram_video.mp4', { 
        type: 'video/mp4' 
      });

      return file;

    } catch (error) {
      console.error('Video download error:', error);
      throw error;
    }
  }

  // NEW: Extract actual video URL from Instagram post URL
  async extractInstagramVideoUrl(instagramUrl) {
    try {
      // This is a simplified approach - in production you'd want a more robust solution // You might need to use Instagram's API or a video extraction service
      
      // For now, we'll try to fetch the Instagram page and extract the video URL
      const response = await fetch(`https://api.instagram.com/oembed/?url=${encodeURIComponent(instagramUrl)}`);
      
      if (response?.ok) {
        const data = await response?.json();
        // Look for video URL in the oEmbed response
        if (data?.thumbnail_url) {
          // This might be a video thumbnail, we need the actual video
          // In a real implementation, you'd extract the video URL from the page HTML
          return data?.thumbnail_url?.replace('_t.jpg', '_v.mp4');
        }
      }

      // Fallback: Try direct video URL patterns
      const videoId = instagramUrl?.match(/\/p\/([A-Za-z0-9_-]+)/)?.[1] || 
                     instagramUrl?.match(/\/reel\/([A-Za-z0-9_-]+)/)?.[1];
      
      if (videoId) {
        // This is a placeholder - you'd need the actual Instagram video CDN URL
        return `https://instagram.com/p/${videoId}/video/`;
      }

      throw new Error('Could not extract video URL from Instagram post');

    } catch (error) {
      console.error('Instagram URL extraction error:', error);
      throw error;
    }
  }

  // NEW: Format video transcript with timestamps
  formatVideoTranscriptForDownload(transcript, segments, contentItem, videoUrl) {
    const timestamp = new Date()?.toISOString()?.replace(/[:.]/g, '-');
    
    let formattedTranscript = `INSTAGRAM VIDEO TRANSCRIPT - ${contentItem?.title || 'Video Content'}
Generated: ${new Date()?.toLocaleString()}
Source: ${videoUrl}
Content Type: ${contentItem?.content_type?.toUpperCase() || 'VIDEO'}
Transcription Method: OpenAI Whisper API

=== FULL VIDEO TRANSCRIPT ===

`;

    // Add timestamped transcript if segments are available
    if (segments && Array?.isArray(segments)) {
      formattedTranscript += "TIMESTAMPED TRANSCRIPT:\n\n";
      segments?.forEach(segment => {
        const startTime = this.formatTimestamp(segment?.start);
        const endTime = this.formatTimestamp(segment?.end);
        formattedTranscript += `[${startTime} - ${endTime}] ${segment?.text?.trim()}\n`;
      });
      
      formattedTranscript += "\n\nFULL TEXT (NO TIMESTAMPS):\n\n";
    }

    // Add the full transcript text
    formattedTranscript += transcript;

    formattedTranscript += `

=== TRANSCRIPT METADATA ===
- Original URL: ${videoUrl}
- Title: ${contentItem?.title || 'No title available'}
- Description: ${contentItem?.description || 'No description available'}
- Transcription Date: ${new Date()?.toLocaleString()}
- Processing Method: Video Download + Whisper API Transcription
- Segments Count: ${segments?.length || 0}

=== END OF TRANSCRIPT ===

Generated by Swirl AI - Video Transcription Platform`;

    return formattedTranscript;
  }

  // Helper: Format timestamp in MM:SS format
  formatTimestamp(seconds) {
    const mins = Math?.floor(seconds / 60);
    const secs = Math?.floor(seconds % 60);
    return `${mins}:${String(secs)?.padStart(2, '0')}`;
  }

  // New method to properly analyze URL content
  async analyzeUrlContent(contentItem) {
    try {
      const systemPrompt = `You are an expert content analyst. Analyze the provided URL and its associated content to create a comprehensive transcript and analysis.

Your task:
1. If this is a video/audio URL, provide a detailed content analysis that describes what would be heard/seen
2. Extract all meaningful information from the URL, title, and description
3. Create a structured transcript-style document
4. Provide insights about the content type, themes, and key messages
5. Include actionable takeaways and recommendations

Format the response as a professional transcript with clear sections and meaningful analysis.`;

      const userPrompt = `Analyze this URL and content:

URL: ${contentItem?.url}
Title: ${contentItem?.title || 'No title available'}
Description: ${contentItem?.description || 'No description available'}
Content Type: ${contentItem?.content_type || 'post'}

Provide a comprehensive analysis and transcript-style breakdown of this content. Focus on extracting meaningful insights and creating actionable content rather than just describing metadata.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 3000
      });

      if (response?.error) {
        throw new Error(response?.error);
      }

      // Format as downloadable transcript
      const timestamp = new Date()?.toISOString()?.replace(/[:.]/g, '-');
      const urlDomain = contentItem?.url ? new URL(contentItem?.url)?.hostname : 'unknown';
      
      let formattedTranscript = `URL CONTENT ANALYSIS - ${contentItem?.title || 'Content'}
Generated: ${new Date()?.toLocaleString()}
Source: ${contentItem?.url}
Domain: ${urlDomain}
Content Type: ${contentItem?.content_type?.toUpperCase() || 'CONTENT'}

=== COMPREHENSIVE URL ANALYSIS ===

${response?.data}

=== CONTENT METADATA ===
- Original URL: ${contentItem?.url}
- Title: ${contentItem?.title || 'No title available'}
- Description: ${contentItem?.description || 'No description available'} 
- Analysis Date: ${new Date()?.toLocaleString()}
- Processing Method: AI URL Content Analysis

=== END OF ANALYSIS ===

Generated by Swirl AI - Content Analysis Platform`;

      return {
        data: formattedTranscript,
        error: null
      };
    } catch (error) {
      console.error('URL Content Analysis Error:', error);
      throw error;
    }
  }

  // New method to handle actual file transcription
  async transcribeActualFile(contentItem) {
    try {
      const file = contentItem?.file || contentItem?.media_file;
      
      if (!file) {
        throw new Error('No file provided for transcription');
      }

      // Check file type and size
      const maxSize = 25 * 1024 * 1024; // 25MB limit for Whisper API
      if (file?.size > maxSize) {
        throw new Error('File too large for transcription. Maximum size is 25MB.');
      }

      // Supported audio/video formats for transcription
      const supportedFormats = [
        'mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'wav', 'webm', 'flac', 'ogg'
      ];
      
      const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();
      
      if (!supportedFormats?.includes(fileExtension)) {
        // If it's not an audio/video file, try to read as text
        return await this.transcribeTextFile(file);
      }

      // Use OpenAI Whisper API for audio/video transcription
      const response = await openai?.audio?.transcriptions?.create({
        file: file,
        model: 'whisper-1',
        response_format: 'verbose_json', // Get detailed response with timestamps
        language: 'en' // Can be auto-detected
      });

      // Format the transcript with timestamps if available
      let formattedTranscript = '';
      
      if (response?.segments && Array.isArray(response?.segments)) {
        formattedTranscript = response?.segments?.map(segment => {
            const startTime = Math.floor(segment?.start / 60) + ':' + String(Math.floor(segment?.start % 60))?.padStart(2,'0');
            return `[${startTime}] ${segment?.text}`;
          })?.join('\n');
      } else {
        formattedTranscript = response?.text || 'Transcription failed';
      }

      return {
        data: this.formatTranscriptForDownload(formattedTranscript, contentItem, 'audio_transcription'),
        error: null
      };
    } catch (error) {
      console.error('File Transcription Error:', error);
      return {
        data: null,
        error: `Transcription failed: ${error?.message}`
      };
    }
  }

  // New method to handle text file reading
  async transcribeTextFile(file) {
    try {
      const textContent = await this.readFileAsText(file);
      
      if (!textContent || textContent?.trim()?.length === 0) {
        throw new Error('File appears to be empty or unreadable');
      }

      // For text files, we'll format and structure the content
      const systemPrompt = `You are transcribing and formatting a text file. Your task is to:

1. Clean up the text formatting
2. Fix any obvious encoding issues
3. Structure the content logically
4. Preserve the original meaning and content
5. Add appropriate sections and headers if the content warrants it

DO NOT change the actual content - only improve formatting and structure.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Please transcribe and format this text file content:\n\n${textContent}` }
      ], {
        model: 'gpt-4o',
        temperature: 0.3, // Low temperature for accurate transcription
        max_tokens: 4000
      });

      const formattedContent = response?.data || textContent;
      
      return {
        data: this.formatTranscriptForDownload(formattedContent, { file }, 'text_transcription'),
        error: null
      };
    } catch (error) {
      console.error('Text File Reading Error:', error);
      return {
        data: null,
        error: `Text file reading failed: ${error?.message}`
      };
    }
  }

  // Helper method to read file as text
  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        resolve(event.target.result);
      };
      
      reader.onerror = (error) => {
        reject(new Error('Failed to read file'));
      };
      
      // Try to read as text
      reader.readAsText(file);
    });
  }

  // Helper method to format transcript for download
  formatTranscriptForDownload(transcript, contentItem, transcriptionType) {
    const timestamp = new Date()?.toISOString()?.replace(/[:.]/g, '-');
    const fileName = contentItem?.file?.name || contentItem?.title || 'content';
    
    return `TRANSCRIPT - ${fileName}
Generated: ${new Date()?.toLocaleString()}
Transcription Type: ${transcriptionType?.replace('_', ' ')?.toUpperCase()}

${transcriptionType === 'audio_transcription' ? '=== AUDIO TRANSCRIPT ===' : '=== TEXT CONTENT ==='}

${transcript}

${transcriptionType === 'audio_transcription' ? '=== END OF TRANSCRIPT ===': '=== END OF CONTENT ==='
}

Generated by Swirl AI - Content Analysis Platform`;
  }

  // New method for content topic analysis
  async analyzeContentForTopic(content, contentType) {
    try {
      if (!this.isConfigured()) {
        return { 
          data: null, 
          error: 'OpenAI API key not configured. Please check your VITE_OPENAI_API_KEY environment variable.' 
        };
      }

      const analysisPrompt = `Analyze the following ${contentType} content and suggest the most appropriate topic category and color theme:

Content: "${content}"

Based on this content, provide a JSON response with:
1. suggestedTopic: A concise, descriptive topic name (2-4 words max)
2. suggestedColor: A hex color code that matches the content theme
3. confidence: Your confidence level (1-10)
4. reasoning: Brief explanation of why this topic fits

Focus on the main subject, theme, or category. Examples:
- Fitness content → "Fitness & Health" - Cooking videos →"Food & Recipes" - Business tips →"Business & Career" - Travel posts →"Travel & Adventure"

Respond ONLY with valid JSON format.`;

      const completion = await openai?.chat?.completions?.create({
        model: "gpt-4o",
        messages: [
          {
            role: "system",
            content: "You are an expert content analyzer. Respond only with valid JSON containing suggestedTopic, suggestedColor, confidence, and reasoning fields."
          },
          {
            role: "user",
            content: analysisPrompt
          }
        ],
        max_tokens: 200,
        temperature: 0.3
      });

      const responseText = completion?.choices?.[0]?.message?.content?.trim();
      
      if (!responseText) {
        throw new Error('Empty response from OpenAI');
      }

      // Parse JSON response
      let analysisResult;
      try {
        analysisResult = JSON.parse(responseText);
      } catch (parseError) {
        // Fallback: extract topic from non-JSON response
        const topicMatch = responseText?.match(/topic["\s]*:?\s*["']([^"']+)["']/i);
        const colorMatch = responseText?.match(/#[0-9A-Fa-f]{6}/);
        
        analysisResult = {
          suggestedTopic: topicMatch?.[1] || 'General',
          suggestedColor: colorMatch?.[0] || '#3B82F6',
          confidence: 7,
          reasoning: 'Extracted from text response'
        };
      }

      // Validate and clean the response
      const validatedResult = {
        suggestedTopic: analysisResult?.suggestedTopic || 'General',
        suggestedColor: this.validateHexColor(analysisResult?.suggestedColor) || '#3B82F6',
        confidence: Math.max(1, Math.min(10, parseInt(analysisResult?.confidence) || 7)),
        reasoning: analysisResult?.reasoning || 'Content analyzed by AI'
      };

      return { data: validatedResult, error: null };

    } catch (error) {
      console.error('Content topic analysis failed:', error);
      
      // Provide fallback categorization
      const fallbackTopic = this.getFallbackTopic(content, contentType);
      
      return { 
        data: {
          suggestedTopic: fallbackTopic?.topic,
          suggestedColor: fallbackTopic?.color,
          confidence: 5,
          reasoning: 'Fallback categorization due to AI analysis error'
        }, 
        error: null 
      };
    }
  }

  // Helper method to validate hex color
  validateHexColor(color) {
    if (!color || typeof color !== 'string') return null;
    
    const hexRegex = /^#[0-9A-Fa-f]{6}$/;
    return hexRegex?.test(color) ? color : null;
  }

  // Fallback topic categorization when AI fails
  getFallbackTopic(content, contentType) {
    const contentLower = content?.toLowerCase() || '';
    
    // Simple keyword-based categorization
    if (contentLower?.includes('fitness') || contentLower?.includes('workout') || contentLower?.includes('exercise')) {
      return { topic: 'Fitness & Health', color: '#10B981' };
    }
    if (contentLower?.includes('food') || contentLower?.includes('recipe') || contentLower?.includes('cooking')) {
      return { topic: 'Food & Recipes', color: '#F59E0B' };
    }
    if (contentLower?.includes('travel') || contentLower?.includes('vacation') || contentLower?.includes('trip')) {
      return { topic: 'Travel & Adventure', color: '#06B6D4' };
    }
    if (contentLower?.includes('business') || contentLower?.includes('work') || contentLower?.includes('career')) {
      return { topic: 'Business & Career', color: '#6366F1' };
    }
    if (contentLower?.includes('tech') || contentLower?.includes('software') || contentLower?.includes('coding')) {
      return { topic: 'Technology', color: '#8B5CF6' };
    }
    if (contentType === 'video' || contentLower?.includes('youtube') || contentLower?.includes('video')) {
      return { topic: 'Videos & Media', color: '#EF4444' };
    }
    
    // Default fallback
    return { topic: 'General', color: '#6B7280' };
  }

  // Helper method to check if OpenAI is configured
  isConfigured() {
    return !!openai?.apiKey;
  }

  // New method for batch file transcription
  async transcribeBatchFiles(files) {
    try {
      if (!files || !Array.isArray(files) || files?.length === 0) {
        throw new Error('No files provided for batch transcription');
      }

      const results = [];
      
      for (let i = 0; i < files?.length; i++) {
        const file = files?.[i];
        // Transcribing file
        
        try {
          const result = await this.transcribeActualFile({ file });
          results?.push({
            fileName: file?.name,
            success: !result?.error,
            data: result?.data,
            error: result?.error
          });
        } catch (error) {
          results?.push({
            fileName: file?.name,
            success: false,
            data: null,
            error: error?.message
          });
        }

        // Add small delay between requests to avoid rate limiting
        if (i < files?.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      return {
        data: results,
        error: null
      };
    } catch (error) {
      console.error('Batch Transcription Error:', error);
      return {
        data: null,
        error: error?.message
      };
    }
  }

  // New method to generate content-based transcript for posts without audio
  async generateContentBasedTranscript(contentItem) {
    try {
      const systemPrompt = `You are generating a content-based transcript for an Instagram post that doesn't have audio/video content. Create a structured document that represents what someone would "hear" or learn from this content.

Format as a transcript-style document with:
- Content overview
- Key points mentioned
- Visual elements description (if applicable)  
- Main message or takeaway
- Relevant hashtags or mentions

Make it informative and useful as if someone was describing the content verbally.`;

      const contentContext = `
Instagram Content Details:
- Title: ${contentItem?.title || 'Instagram Content'}
- Description: ${contentItem?.description || 'No description available'}
- Content Type: ${contentItem?.content_type || 'post'}
- URL: ${contentItem?.url}
- Likes: ${contentItem?.likes_count || 0}
- Comments: ${contentItem?.comments_count || 0}

Generate a transcript-style content analysis for this Instagram post.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: contentContext }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2000
      });

      return {
        data: response?.data || `Content Analysis Transcript - ${contentItem?.title || 'Instagram Content'}

[Content Overview]
This Instagram post contains textual and visual content rather than audio/video.

[Main Content]
Title: ${contentItem?.title || 'No title available'}
Description: ${contentItem?.description || 'No description available'}

[Key Points]
- Content Type: ${contentItem?.content_type || 'post'}
- Engagement: ${contentItem?.likes_count || 0} likes, ${contentItem?.comments_count || 0} comments
- Source URL: ${contentItem?.url || 'N/A'}

[Notes]
This content analysis serves as a transcript alternative for Instagram posts without audio content.`,
        error: null
      };
    } catch (error) {
      console.error('Content-based transcript generation failed:', error);
      throw error;
    }
  }

  // Generate downloadable summary for any content
  async generateDownloadableSummary(contentItem, existingAnalysis = null) {
    try {
      const systemPrompt = `You are generating a downloadable summary document for Instagram content. Create a comprehensive, well-formatted summary that can be saved as a text document.

Structure the summary with:
- Executive Summary (2-3 sentences)
- Key Insights (bullet points)
- Action Items (numbered list)
- Performance Metrics (if available)
- Recommendations (bullet points)
- Conclusion

Make it professional and suitable for saving as a document.`;

      const contentContext = `
Content Details:
- Title: ${contentItem?.title || 'Instagram Content'}
- Description: ${contentItem?.description || 'No description available'}
- URL: ${contentItem?.url}
- Content Type: ${contentItem?.content_type || 'post'}
- Likes: ${contentItem?.likes_count || 0}
- Comments: ${contentItem?.comments_count || 0}
- Created: ${contentItem?.created_at}
${existingAnalysis ? `- Previous Analysis: ${existingAnalysis}` : ''}

Generate a comprehensive downloadable summary of this content.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: contentContext }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2000
      });

      return response;
    } catch (error) {
      console.error('Downloadable Summary Error:', error);
      return {
        data: 'Unable to generate downloadable summary at this time. Please try again.',
        error: error?.message
      };
    }
  }

  // Simulate transcript for Instagram video content
  async generateSimulatedTranscript(contentItem) {
    try {
      const systemPrompt = `You are generating a simulated transcript for Instagram content. Based on the content details provided, create a realistic transcript that might represent what was said in the video/audio.

Format as a proper transcript with:
- Timestamps (approximate)
- Speaker identification if multiple people
- Clear sentence structure
- Natural speech patterns

Make it realistic and relevant to the content type and description.`;

      const contentContext = `
Content Details:
- Title: ${contentItem?.title || 'Instagram Content'}
- Description: ${contentItem?.description || 'No description available'}
- Content Type: ${contentItem?.content_type || 'post'}
- URL: ${contentItem?.url}

Generate a realistic transcript for this Instagram content.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: contentContext }
      ], {
        model: 'gpt-4o',
        temperature: 0.8,
        max_tokens: 2000
      });

      return response?.data || 'Unable to generate transcript for this content.';
    } catch (error) {
      console.error('Simulated Transcript Error:', error);
      return 'Transcript generation failed. Please try again.';
    }
  }

  // Extract key quotes and highlights for download
  async extractKeyQuotes(contentItem, transcript) {
    try {
      const systemPrompt = `Extract the most important quotes, key messages, and highlights from this Instagram content transcript. 

Format as:
- Top 5 Key Quotes (with context)
- Main Messages (bullet points)
- Notable Highlights (numbered list)
- Actionable Statements (if any)

Focus on the most memorable and impactful content.`;

      const response = await this.createChatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Content: ${contentItem?.title}\nTranscript: ${transcript}` }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 1500
      });

      return response;
    } catch (error) {
      console.error('Key Quotes Extraction Error:', error);
      return {
        data: 'Unable to extract key quotes at this time.',
        error: error?.message
      };
    }
  }

  // NEW: Check if URL is a video that can be transcribed
  isVideoUrl(url) {
    if (!url) return false;
    
    const videoPatterns = [
      /instagram\.com\/p\/[^\/]+/,      // Instagram posts
      /instagram\.com\/reel\/[^\/]+/,   // Instagram reels
      /youtube\.com\/watch\?v=/,       // YouTube videos
      /youtu\.be\/[^\/]+/,            // YouTube short links
      /tiktok\.com\/@[^\/]+\/video\//, // TikTok videos
    ];

    return videoPatterns?.some(pattern => pattern?.test(url));
  }

  // Generate comprehensive folder analysis for collections/channels
  async generateFolderAnalysis(folderId, folderType, folderName, allContent) {
    try {
      console.log(`🧠 Generating folder analysis for ${folderType}: ${folderName}`);
      
      const contentSummaries = allContent.map((item, index) => ({
        id: item.id,
        title: item.title,
        summary: item.ai_analysis?.summary || 'No summary available',
        key_points: item.ai_analysis?.key_points || [],
        tags: item.tags || [],
        channel: item.channel,
        content_type: item.content_type,
        created_at: item.created_at
      }));

      const prompt = `Analyze this collection of content and create a comprehensive folder analysis:

FOLDER: ${folderName} (${folderType})
TOTAL ITEMS: ${allContent.length}

CONTENT ITEMS:
${contentSummaries.map((item, i) => `
${i+1}. ${item.title}
   Summary: ${item.summary}
   Key Points: ${item.key_points?.join(', ')}
   Tags: ${item.tags?.join(', ')}
   Channel: ${item.channel}
   Type: ${item.content_type}
`).join('\n')}

Please provide a JSON response with:
{
  "collective_summary": "comprehensive 3-4 sentence summary of the entire collection",
  "key_themes": ["theme1", "theme2", "theme3"],
  "top_tags": ["tag1", "tag2", "tag3"],
  "content_breakdown": {
    "by_type": {"reel": 5, "post": 3, "story": 2},
    "by_topic": {"Marketing": 4, "Product": 3, "Customer": 3}
  },
  "recent_highlights": ["highlight1", "highlight2", "highlight3"],
  "insights": ["insight1", "insight2", "insight3"],
  "suggested_questions": ["question1", "question2", "question3"],
  "confidence_score": 0.95,
  "last_analyzed": "${new Date().toISOString()}"
}`;

      const response = await this.createChatCompletion([
        {
          role: 'system',
          content: 'You are an expert content analyst specializing in collection analysis. Always respond with valid JSON only, no additional text.'
        },
        {
          role: 'user',
          content: prompt
        }
      ], {
        temperature: 0.2,
        max_tokens: 1200
      });

      if (response?.error) {
        throw new Error(response.error);
      }

      let analysis;
      try {
        analysis = JSON.parse(response.data);
      } catch (parseError) {
        analysis = {
          collective_summary: `This ${folderType} contains ${allContent.length} items covering various topics.`,
          key_themes: ['General Content'],
          top_tags: ['content'],
          content_breakdown: { by_type: {}, by_topic: {} },
          recent_highlights: ['Content available'],
          insights: ['Collection contains diverse content'],
          suggested_questions: ['What topics are covered?', 'What are the main themes?'],
          confidence_score: 0.5,
          last_analyzed: new Date().toISOString()
        };
      }

      return {
        data: analysis,
        error: null
      };
    } catch (error) {
      console.error('Folder Analysis Error:', error);
      return {
        data: null,
        error: error.message
      };
    }
  }
}

export default new OpenAIService();